import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Connection,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  OnEdgesChange,
  OnNodesChange,
  XYPosition,
} from 'reactflow';
import { EntityData } from '@components/MainStage/types';
import { NodesTypes } from '@constants/canvas/general';
import { EntityValidationStatus } from '@constants/canvas/layers';
import { DependencyTypes, WeightTypes } from '@constants/entities/relationship';
import { nanoid } from '@reduxjs/toolkit';
import { EdgeDTO } from '@store/services/nodes/types';
import { findMaxCoordinates } from '@utils/helpers';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import {
  ActiveSelectionState,
  NodeItemState,
  selectActiveSelection,
  selectNodeItemState,
} from './store.selectors';

/**
 * The reactflow graph plus the actions the canvas drives it with.
 *
 * Nodes arrive from `formatNodeDTOToCanvasNode` without a `selected` key, so
 * selection is `undefined` until something selects for the first time; compare
 * it as a boolean, never with `===` against `true`/`false`.
 */
export type RFState = {
  nodes: Node[];
  edges: Edge[];
  phantoms: Edge[];
  getNodes: () => Node[];
  setNodes: (nodes: Node<any, string | undefined>[]) => void;
  setEdges: (edges: Edge[]) => void;
  setPhantoms: (phantoms: Edge[]) => void;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onConnect: (connection: Connection) => false | string;
  addNode: (node: Node) => void;
  getActiveNode: () => Node | undefined;
  getNodeById: (id: string) => Node | undefined;
  getActiveEdge: () => Edge<EntityData<EdgeDTO>> | undefined;
  setActiveNode: (nodeId: string) => void;
  selectNodeAndRemoveLocal: (nodeId: string) => void;
  selectNode: (nodeId: string) => void;
  selectEdge: (edgeId: string) => void;
  unselectActiveNode: () => void;
  unselectActiveEdge: () => void;
  clearLocalNodes: () => void;
  clearLocalEdges: () => void;
  deleteNode: (nodeId: string) => void;
  updateNodeLabel: (nodeId: string, label: string) => void;
  updateNodePosition: (nodeId: string, position: object) => void;
  updateNodePositions: (
    positions: { id: string; x?: number; y?: number }[],
  ) => void;
  updateNode: (nodeId: string, data: object) => void;
  addChildNode: (
    parentNodeId: string,
    position: XYPosition,
    selected?: boolean,
  ) => string;
  getIsNodeTargetConnected: (nodeId: string) => boolean;
  getIsNodeSourceConnected: (nodeId: string) => boolean;
  getLastNodeCoordinates: () => { x: number; y: number };
};

// this is our useStore hook that we can use in our components to get parts of the store and call actions
const useStore = createWithEqualityFn<RFState>((set, get) => {
  return {
    nodes: [],
    edges: [],
    phantoms: [],
    getNodes: () => get().nodes,
    onNodesChange: (changes: NodeChange[]) => {
      set({
        nodes: applyNodeChanges(changes, get().nodes),
      });
    },
    onEdgesChange: (changes: EdgeChange[]) => {
      const applicable = changes.filter(
        (change) =>
          !(
            change.type === 'select' &&
            !change.selected &&
            change.id.startsWith('local')
          ),
      );

      set({
        edges: applyEdgeChanges(applicable, get().edges),
      });
    },
    onConnect: (connection: Connection) => {
      const existConnection = get().edges.find(
        (edge) =>
          edge.source === connection.source &&
          edge.target === connection.target,
      );

      if (existConnection) return false;

      const id = `local-${nanoid(10)}`;

      set({
        edges: addEdge(
          {
            id,
            selected: true,
            animated: true,
            ...connection,
            data: {
              dto: {
                dependency: DependencyTypes.AND,
                weight: 0,
                weight_type: WeightTypes.Auto,
                parent_id: connection.source,
                child_id: connection.target,
              },
              canvas: {},
            },
          },
          get().edges,
        ),
      });

      set({
        nodes: get().nodes.map((node) => ({
          ...node,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              selectedWithEdge:
                node.id === connection.source || node.id === connection.target,
            },
          },
        })),
      });

      return id;
    },
    updateNodeData(id: string, data: object) {
      set({
        nodes: get().nodes.map((node) =>
          node.id === id ? { ...node, data: { ...node.data, ...data } } : node,
        ),
      });
    },
    updateNodePosition(id: string, position: object) {
      const { nodes } = get();
      const target = nodes.find((node) => node.id === id);

      if (!target) return;

      const next = { ...target.position, ...(position as Partial<XYPosition>) };

      if (next.x === target.position.x && next.y === target.position.y) return;

      set({
        nodes: nodes.map((node) =>
          node.id === id ? { ...node, position: next } : node,
        ),
      });
    },
    updateNodePositions(positions) {
      const byId = new Map(positions.map((update) => [update.id, update]));

      set({
        nodes: get().nodes.map((node) => {
          const update = byId.get(node.id);

          return update
            ? {
                ...node,
                position: {
                  ...node.position,
                  ...(update.x !== undefined && { x: update.x }),
                  ...(update.y !== undefined && { y: update.y }),
                },
              }
            : node;
        }),
      });
    },
    updateNode(id: string, data: object) {
      set({
        nodes: get().nodes.map((node) =>
          node.id === id ? { ...node, ...data } : node,
        ),
      });
    },
    getActiveNode: () => {
      return get().nodes.find((node: Node) => node.selected);
    },
    getNodeById: (id: string) => {
      return get().nodes.find((node: Node) => node.id === id);
    },
    getActiveEdge: () => {
      return get().edges.find((edge: Edge) => edge.selected);
    },
    setActiveNode: (nodeId: string) => {
      const { nodes } = get();
      if (
        nodes.every((node) => Boolean(node.selected) === (node.id === nodeId))
      )
        return;

      set({
        nodes: nodes.map((node) => ({
          ...node,
          selected: node.id === nodeId,
        })),
      });
    },

    selectNodeAndRemoveLocal: (nodeId: string) => {
      const { nodes, edges } = get();

      set({
        nodes: nodes.reduce<Node[]>((newNodes, node) => {
          if (node.id.startsWith('local')) {
            return newNodes;
          }

          newNodes.push({
            ...node,
            selected: node.id === nodeId,
          });

          return newNodes;
        }, []),

        edges: edges.reduce<Edge[]>((newEdges, edge) => {
          if (edge.id.startsWith('local')) {
            return newEdges;
          }

          newEdges.push({
            ...edge,
            selected: false,
            animated: false,
          });

          return newEdges;
        }, []),
      });
    },

    addChildNode: (
      parentNodeId: string,
      position: XYPosition,
      selected = true, // when user add a child node, it should become active
    ) => {
      const id = `local-${nanoid()}`;

      const newNode: Node = {
        id,
        type: NodesTypes.NewNode,
        data: {
          dto: {
            domain_id: null,
            valid_status: EntityValidationStatus.Draft,
            review_required: false,
          },
          canvas: {},
        },
        position,
        parentNode: parentNodeId,
        extent: 'parent',
        selected,
      };

      set({
        nodes: [...get().nodes, newNode],
      });

      return id;
    },
    clearLocalNodes: () => {
      set({
        nodes: get().nodes.filter((node) => !node.id.startsWith('local')),
      });
    },
    clearLocalEdges: () => {
      set({
        edges: get().edges.filter((edge) => !edge.id.startsWith('local')),
      });
    },
    addNode: (newNode: Node) => {
      set({
        nodes: [...get().nodes, newNode],
      });
    },
    deleteNode: (id: string) => {
      set({
        nodes: get().nodes.filter((node) => node.id !== id),
        edges: get().edges.filter((edge) => edge.source !== id),
      });
    },

    selectNode: (nodeId: string) => {
      const { nodes } = get();
      if (
        nodes.every((node) => Boolean(node.selected) === (node.id === nodeId))
      )
        return;

      set({
        nodes: nodes.map((node) => ({
          ...node,
          selected: node.id === nodeId,
        })),
      });
    },

    selectEdge: (edgeId: string) => {
      const { nodes, edges } = get();
      const selectedEdge = edges.find((edge) => edge.id === edgeId);
      const sourceNode = nodes.find((node) => node.id === selectedEdge?.source);
      const targetNode = nodes.find((node) => node.id === selectedEdge?.target);

      set({
        nodes: nodes.map((node) => ({
          ...node,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              selectedWithEdge:
                node.id === targetNode?.id || node.id === sourceNode?.id,
            },
          },
        })),
        // Not `animated`: the selected relationship is a static dash, and React
        // Flow's .animated rule would make it crawl.
        edges: edges.map((edge) => ({
          ...edge,
          selected: edge.id === edgeId,
        })),
      });
    },

    unselectActiveNode() {
      const { nodes } = get();
      if (!nodes.some((node) => node.selected)) return;

      set({
        nodes: nodes.map((node) => ({ ...node, selected: false })),
      });
    },

    unselectActiveEdge() {
      const { nodes, edges } = get();

      const hasSelectedEdge = edges.some(
        (edge) => edge.selected || edge.animated,
      );
      const hasHighlightedNode = nodes.some(
        (node) => node.data?.canvas?.selectedWithEdge,
      );

      if (!hasSelectedEdge && !hasHighlightedNode) return;

      set({
        edges: hasSelectedEdge
          ? edges.map((edge) => ({
              ...edge,
              selected: false,
              animated: false,
            }))
          : edges,
        nodes: hasHighlightedNode
          ? nodes.map((node) => ({
              ...node,
              data: {
                ...node.data,
                canvas: {
                  ...node.data.canvas,
                  selectedWithEdge: false,
                },
              },
            }))
          : nodes,
      });
    },

    setNodes: (nodes: Node[]) => {
      set({
        nodes: [...nodes],
      });
    },
    setEdges: (edges: Edge[]) => {
      set({
        edges: [...edges],
      });
    },

    setPhantoms: (phantoms: Edge[]) => {
      set({ phantoms });
    },

    getIsNodeTargetConnected: (nodeId: string) => {
      const { edges } = get();

      return !!edges.find((edge) => edge.target === nodeId);
    },
    getIsNodeSourceConnected: (nodeId: string) => {
      const { edges } = get();

      return !!edges.find((edge) => edge.source === nodeId);
    },
    getLastNodeCoordinates: () => {
      const { nodes } = get();
      const customNodes = nodes.filter(
        (node) => node.type === NodesTypes.CustomNode,
      );

      return findMaxCoordinates(customNodes);
    },
    updateNodeLabel: (nodeId: string, label: string) => {
      const { nodes } = get();
      const target = nodes.find((node) => node.id === nodeId);

      if (!target || target.data?.dto?.label === label) return;

      set({
        nodes: nodes.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                data: { ...node.data, dto: { ...node.data.dto, label } },
              }
            : node,
        ),
      });
    },
  };
});

export const useNodeItemState = (id: string): NodeItemState =>
  useStore(selectNodeItemState(id), shallow);

export const useActiveSelection = (id: string): ActiveSelectionState =>
  useStore(selectActiveSelection(id), shallow);

export const canvasState = useStore.getState;
export default useStore;
